"use client";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createLoyaltyPackageEndpoint,
  updateLoyaltyPackageEndpoint,
  getAllLoyaltyPackagesEndpoint,
  getLoyaltyPackageByIdEndpoint,
  deleteLoyaltyPackageEndpoint,
  revokeLoyaltyCodeEndpoint,
  getLoyaltyStatisticsEndpoint,
  validateLoyaltyCodeEndpoint,
  applyLoyaltyCodeEndpoint,
} from "../endpoints/loyaltyEndpoints";
import {
  ICreateLoyaltyPackageRequest,
  ICreateLoyaltyPackageResponse,
  IUpdateLoyaltyPackageRequest,
  IUpdateLoyaltyPackageResponse,
  IGetAllLoyaltyPackagesResponse,
  IGetLoyaltyPackageByIdResponse,
  IDeleteLoyaltyPackageResponse,
  IRevokeLoyaltyCodeResponse,
  ILoyaltyStatisticsResponse,
  IValidateLoyaltyCodeRequest,
  IValidateLoyaltyCodeResponse,
  IApplyLoyaltyCodeRequest,
  IApplyLoyaltyCodeResponse,
} from "../../../types";

// CMS Hooks - Za zaposlenike
export const useCreateLoyaltyPackage = (): UseMutationResult<
  ICreateLoyaltyPackageResponse,
  unknown,
  ICreateLoyaltyPackageRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ICreateLoyaltyPackageRequest) => {
      const response: AxiosResponse<ICreateLoyaltyPackageResponse> = await httpClient.post(
        createLoyaltyPackageEndpoint(),
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllLoyaltyPackages"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyStatistics"] });
      toast.success("Loyalty paket je uspješno kreiran");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Greška pri kreiranju loyalty paketa");
    },
  });
};

export const useUpdateLoyaltyPackage = (): UseMutationResult<
  IUpdateLoyaltyPackageResponse,
  unknown,
  { id: string; data: IUpdateLoyaltyPackageRequest },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: IUpdateLoyaltyPackageRequest }) => {
      const response: AxiosResponse<IUpdateLoyaltyPackageResponse> = await httpClient.put(
        updateLoyaltyPackageEndpoint(id),
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllLoyaltyPackages"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyPackageById"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyStatistics"] });
      toast.success("Loyalty paket je uspješno ažuriran");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Greška pri ažuriranju loyalty paketa");
    },
  });
};

export const useGetAllLoyaltyPackages = (search?: string): UseQueryResult<IGetAllLoyaltyPackagesResponse, unknown> => {
  return useQuery({
    queryKey: ["getAllLoyaltyPackages", search],
    queryFn: async () => {
      const endpoint = getAllLoyaltyPackagesEndpoint();
      const url = search ? `${endpoint}?search=${encodeURIComponent(search)}` : endpoint;
      const response: AxiosResponse<IGetAllLoyaltyPackagesResponse> = await httpClient.get(url);
      return response.data;
    },
    retry: 1,
  });
};

export const useGetLoyaltyPackageById = (id: string): UseQueryResult<IGetLoyaltyPackageByIdResponse, unknown> => {
  return useQuery({
    queryKey: ["getLoyaltyPackageById", id],
    queryFn: async () => {
      const response: AxiosResponse<IGetLoyaltyPackageByIdResponse> = await httpClient.get(
        getLoyaltyPackageByIdEndpoint(id)
      );
      return response.data;
    },
    enabled: !!id,
    retry: 1,
  });
};

export const useDeleteLoyaltyPackage = (): UseMutationResult<
  IDeleteLoyaltyPackageResponse,
  unknown,
  string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response: AxiosResponse<IDeleteLoyaltyPackageResponse> = await httpClient.delete(
        deleteLoyaltyPackageEndpoint(id)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllLoyaltyPackages"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyStatistics"] });
      toast.success("Loyalty paket je uspješno obrisan");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Greška pri brisanju loyalty paketa");
    },
  });
};

export const useRevokeLoyaltyCode = (): UseMutationResult<
  IRevokeLoyaltyCodeResponse,
  unknown,
  string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (usageId: string) => {
      const response: AxiosResponse<IRevokeLoyaltyCodeResponse> = await httpClient.post(
        revokeLoyaltyCodeEndpoint(usageId)
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate loyalty related queries
      queryClient.invalidateQueries({ queryKey: ["getAllLoyaltyPackages"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyPackageById"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyStatistics"] });
      
      // Invalidate reservation queries to refresh the reservation data
      queryClient.invalidateQueries({ queryKey: ["getReservationBy"] });
      queryClient.invalidateQueries({ queryKey: ["getAllReservations"] });
      
      toast.success("Loyalty kod je uspješno povračen");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Greška pri povratku loyalty koda");
    },
  });
};

export const useGetLoyaltyStatistics = (): UseQueryResult<ILoyaltyStatisticsResponse, unknown> => {
  return useQuery({
    queryKey: ["getLoyaltyStatistics"],
    queryFn: async () => {
      const response: AxiosResponse<ILoyaltyStatisticsResponse> = await httpClient.get(
        getLoyaltyStatisticsEndpoint()
      );
      return response.data;
    },
    retry: 1,
  });
};

export const useValidateLoyaltyCode = (): UseMutationResult<
  IValidateLoyaltyCodeResponse,
  unknown,
  IValidateLoyaltyCodeRequest,
  unknown
> => {
  return useMutation({
    mutationFn: async (data: IValidateLoyaltyCodeRequest) => {
      const response: AxiosResponse<IValidateLoyaltyCodeResponse> = await httpClient.post(
        validateLoyaltyCodeEndpoint(),
        data
      );
      return response.data;
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Kod nije validan");
    },
  });
};

export const useApplyLoyaltyCode = (): UseMutationResult<
  IApplyLoyaltyCodeResponse,
  unknown,
  IApplyLoyaltyCodeRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IApplyLoyaltyCodeRequest) => {
      const response: AxiosResponse<IApplyLoyaltyCodeResponse> = await httpClient.post(
        applyLoyaltyCodeEndpoint(),
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate loyalty related queries
      queryClient.invalidateQueries({ queryKey: ["getAllLoyaltyPackages"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyPackageById"] });
      queryClient.invalidateQueries({ queryKey: ["getLoyaltyStatistics"] });
      
      // Invalidate reservation queries to refresh the reservation data
      queryClient.invalidateQueries({ queryKey: ["getReservationBy"] });
      queryClient.invalidateQueries({ queryKey: ["getAllReservations"] });
      
      toast.success("Loyalty kod je uspješno primijenjen");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Greška pri primjeni loyalty koda");
    },
  });
};